import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useFormatUpdate, FormatUpdateInput } from "@/graphql/hooks/useFormatUpdate"
import { useAllGames } from "@/graphql/hooks/useGames"
import { useToast } from "@/hooks/use-toast"
import type { TournamentFormat } from "@/graphql/hooks/useAllFormats"
import { Ban, Check } from "lucide-react"

interface FormatEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  format: TournamentFormat | null
  onSuccess: () => void
}

export function FormatEditModal({ open, onOpenChange, format, onSuccess }: FormatEditModalProps) {
  const { updateFormat, loading } = useFormatUpdate()
  const { games } = useAllGames(false)
  const { toast } = useToast()
  const [formData, setFormData] = useState<FormatUpdateInput>({
    id: 0,
    name: "",
    description: "",
    status: true,
    gameIds: [],
  })

  useEffect(() => {
    if (format) {
      setFormData({
        id: format.id,
        name: format.name,
        description: "",
        status: format.status === 1,
        gameIds: format.FormatGames?.map((fg) => fg.gameId) || [],
      })
    }
  }, [format])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!format) return

    try {
      const result = await updateFormat(formData)
      if (result?.status?.success) {
        toast({
          title: "Formato actualizado",
          variant: "success",
          description: "El formato se ha actualizado correctamente",
        })
        onSuccess()
        onOpenChange(false)
      } else {
        throw new Error(result?.status?.detail || "Error desconocido")
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al actualizar el formato",
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[625px]">
        <DialogHeader>
          <DialogTitle>Editar Formato</DialogTitle>
          <DialogDescription>Modifica la información del formato de juego</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="space-y-3">
            <Label>
              Juegos Asociados
              <br />
              <Check className="h-3 w-3 mx-1 inline" /> activados
              <Ban className="h-3 w-3 mx-1 inline" /> desactivados
            </Label>
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {games.map((game) => (
                <div key={game.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`game-${game.id}`}
                    checked={formData.gameIds?.includes(game.id) || false}
                    onCheckedChange={(checked) => {
                      const currentGameIds = formData.gameIds || []
                      if (checked) {
                        setFormData({
                          ...formData,
                          gameIds: [...currentGameIds, game.id],
                        })
                      } else {
                        setFormData({
                          ...formData,
                          gameIds: currentGameIds.filter((id) => id !== game.id),
                        })
                      }
                    }}
                  />
                  <Label htmlFor={`game-${game.id}`} className="text-sm flex">
                    {game.name}
                    {game.isActive ? (
                      <Check className="h-3 w-3 mx-1 inline" />
                    ) : (
                      <Ban className="h-3 w-3 mx-1 inline" />
                    )}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="status"
              checked={formData.status}
              onCheckedChange={(checked) => setFormData({ ...formData, status: checked })}
            />
            <Label htmlFor="status">Activo</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
